import React, { useEffect, useContext } from 'react';
import proptypes from 'prop-types';
const { string, shape, arrayOf, bool } = proptypes;
import { Alert } from '@one-thd/sui-atomic-components';
import { QuantityAdjustmentsContext } from '../QuantityAdjustmentsContext';

function createItemBackorderedMessage(itemMap, itemId) {
  return {
    variant: 'warning',
    children: `Backordered${itemMap[itemId].backOrderDate ? ` until ${itemMap[itemId].backOrderDate}` : ''}`
  };
}

function createItemAvailabilityResponse(itemAvailableQty) {
  return {
    variant: 'warning',
    children: `There are only ${itemAvailableQty} in stock at this time. Please reduce the quantity to proceed.`
  };
}

function createQuantityLimitResponse(itemMap, itemId) {
  return {
    variant: 'warning',
    children: `This item has a quantity limit of ${itemMap[itemId].quantityLimit}. Please reduce the quantity to proceed.`
  };
}

function handleOutOfStockLogic(itemMap) {
  const itemMapValues = Object.values(itemMap);
  const outOfStockQty = itemMapValues.filter(({ availableQty }) => !availableQty).length;

  if (outOfStockQty) {
    return {
      variant: 'error',
      children: `${outOfStockQty} of ${itemMapValues.length} items are out of stock`
    };
  }

  return null;
}

const SetProducts = (products, podFulFillmentUtils) => {
  const { itemMap, setItemMap, setFulfillmentFetched } = useContext(QuantityAdjustmentsContext);
  useEffect(() => {
    const mappedProducts = products.reduce((map, product) => {
      const { ShipToHome, BOPIS } = podFulFillmentUtils.getAllFulfillmentInventories(product);
      const availableQty = (ShipToHome || BOPIS) || podFulFillmentUtils.getSellableQuantityNearbyStore(product);
      const fulfillmentMethod = ShipToHome ? 'ShipToHome' : 'BOPIS';
      const isBackordered = podFulFillmentUtils.isBackordered(product);
      const isBackorderedWithDate = podFulFillmentUtils.isBackorderedWithDate(product);

      return {
        ...map,
        [product.itemId]: {
          ...map[product.itemId],
          availableQty,
          fulfillmentMethod,
          quantityLimit: product.info?.quantityLimit,
          isBackordered,
          backOrderDate: isBackorderedWithDate || null,
        }
      };
    }, itemMap);

    setItemMap(mappedProducts);
    setFulfillmentFetched(true);
  }, [products]);
};

export const FulfillmentAlert = ({
  products, itemId, podFulFillmentUtils, displayTotalCountAlert, disableUI
}) => {
  const { itemMap } = useContext(QuantityAdjustmentsContext);

  SetProducts(products, podFulFillmentUtils);

  const getAlertTypeProps = () => {
    if (disableUI) {
      return null;
    }

    if (displayTotalCountAlert) {
      return handleOutOfStockLogic(itemMap);
    }

    if (itemMap[itemId]?.isBackordered) {
      return createItemBackorderedMessage(itemMap, itemId);
    }
    const itemAvailableQty = itemMap[itemId]?.availableQty;

    if (!itemAvailableQty) {
      return { variant: 'error', children: 'This product is out of stock' };
    }
    if (itemMap[itemId]?.quantityLimit
      && itemMap[itemId].quantityLimit > 0
      && itemMap[itemId]?.selectedQty > itemMap[itemId].quantityLimit) {
      return createQuantityLimitResponse(itemMap, itemId);
    }
    if (itemAvailableQty < itemMap[itemId]?.selectedQty) {
      return createItemAvailabilityResponse(itemAvailableQty);
    }
    return null;
  };
  const alertTypeProps = getAlertTypeProps();

  return (
    <>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      {alertTypeProps ? <Alert {...alertTypeProps} /> : <div />}
    </>
  );
};

FulfillmentAlert.propTypes = {
  itemId: string,
  products: arrayOf(shape({})),
  podFulFillmentUtils: shape({}),
  displayTotalCountAlert: bool,
  disableUI: bool
};

FulfillmentAlert.defaultProps = {
  itemId: '',
  products: [],
  podFulFillmentUtils: {},
  displayTotalCountAlert: false,
  disableUI: false
};

FulfillmentAlert.displayName = 'FulfillmentAlert';
